import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import userIcon from "../assets/icons/users.png";
import api from "../api";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BASE_URL = `${process.env.REACT_APP_API_URL}/tickets/enterprise-users`;

const TicketNumbers = () => {
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);

  const [stats, setStats] = useState({
    totalTickets: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchStats = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: debouncedSearchTerm || undefined,
        },
      });
      setStats((prev) => ({
        ...prev,
        totalTickets: Number(res.data?.totalTickets || 0) || 0,
      }));
    } catch (e) {
      // non-blocking (page still works without stats)
      console.error("Failed to fetch ticket stats", e);
    }
  };

  const fetchTickets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE_URL}?page=${currentPage}&limit=${entriesPerPage}&search=${encodeURIComponent(
          debouncedSearchTerm,
        )}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Failed to fetch tickets");
      const data = await res.json();
      setTickets(Array.isArray(data.data) ? data.data : []);
      setTotalEntries(Number(data.total || 0) || 0);
    } catch (e) {
      setError(e?.message || "Failed to load tickets");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, currentPage, entriesPerPage]);

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage) || 1;
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);

  const currentData = useMemo(() => {
    return tickets
      .filter((t) => {
        if (!dayFilter) return true;
        const today = new Date();
        const d = new Date(t.updated_timestamp || t.created_timestamp);
        if (dayFilter === "Today") {
          return d.toDateString() === today.toDateString();
        }
        if (dayFilter === "Yesterday") {
          const y = new Date(today);
          y.setDate(today.getDate() - 1);
          return d.toDateString() === y.toDateString();
        }
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.updated_timestamp || a.created_timestamp);
        const dateB = new Date(b.updated_timestamp || b.created_timestamp);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
  }, [tickets, dayFilter, sortOrder]);

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const exportExcel = () => {
    const wsData = [
      [
        "Ticket #",
        "User ID",
        "Email",
        "Title",
        "Created Timestamp",
        "Updated Timestamp",
        "Stack Trace Error/Reason",
      ],
      ...currentData.map((t) => [
        t.ticket_number,
        t.user_fid,
        t.user_email,
        t.title,
        formatDateToUserTimeZone(t.created_timestamp),
        formatDateToUserTimeZone(t.updated_timestamp),
        t.error_details,
      ]),
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "tickets.xlsx");
  };

  const getVisiblePages = () => {
    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start < 1) {
      start = 1;
      end = Math.min(3, totalPages);
    }
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, totalPages - 2);
    }

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="mx-2 pt-1 h-100 overflow-hidden">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {[{ key: "totalTickets", label: "Total Tickets" }].map((item) => (
          <div
            key={item.key}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              <img
                src={userIcon}
                alt={item.label}
                style={{ width: "22px", height: "22px" }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{stats?.[item.key] ?? 0}</h2>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
        <span className="fw-bold refresh-prompts ak-text-lg">Ticket Numbers</span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Tickets"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div>
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              id="ticketFilterDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              aria-labelledby="ticketFilterDropdown"
              style={{ minWidth: "220px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="sortOrder"
                  id="ticket-asc"
                  value="asc"
                  checked={sortOrder === "asc"}
                  onChange={() => setSortOrder("asc")}
                />
                <label className="form-check-label notification-label" htmlFor="ticket-asc">
                  Ascending
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="sortOrder"
                  id="ticket-desc"
                  value="desc"
                  checked={sortOrder === "desc"}
                  onChange={() => setSortOrder("desc")}
                />
                <label className="form-check-label notification-label" htmlFor="ticket-desc">
                  Descending
                </label>
              </div>

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday"].map((day) => (
                <div className="form-check" key={day}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={`ticket-day-${day}`}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label fw-5 notification-label" htmlFor={`ticket-day-${day}`}>
                    {day}
                  </label>
                </div>
              ))}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="dayFilter"
                  id="ticket-day-all"
                  value=""
                  checked={dayFilter === ""}
                  onChange={() => setDayFilter("")}
                />
                <label className="form-check-label fw-5 notification-label" htmlFor="ticket-day-all">
                  All
                </label>
              </div>

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => {}}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">Loading…</p>
        </div>
      ) : error ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">{error}</p>
        </div>
      ) : totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table activity-table table-sm w-100">
              <thead>
                <tr>
                  <SortableHeader
                    column="ticket_number"
                    label="Ticket #"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Title</th>
                  <th>Stack Trace Error/Reason</th>
                  <SortableHeader
                    column="updated_timestamp"
                    label="Timestamp"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedData.map((t) => (
                  <tr key={t._id || t.ticket_number}>
                    <td>{t.ticket_number}</td>
                    <td>{t.user_fid}</td>
                    <td>{t.user_email}</td>
                    <td title={t.title}>{t.title}</td>
                    <td title={t.error_details}>
                      {String(t.error_details || "").length > 140
                        ? `${String(t.error_details || "").slice(0, 140)}…`
                        : String(t.error_details || "")}
                    </td>
                    <td>{formatDateToUserTimeZone(t.updated_timestamp || t.created_timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="refresh-prompts ak-text-sm">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                </li>
                {getVisiblePages().map((p) => (
                  <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(p)}>
                      {p}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketNumbers;
